import { Button, Form, Input, InputNumber, Radio, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';

import callApi from '../../Api/config';
import {
  APPLICATION_FORM_PACKAGES,
  APPLICATION_FORM_SERVICES,
  PACKAGE_CONTENT_MANAGEMENT,
  SERVICE_CONTENT_MANAGEMENT,
  SERVICE_CONTENT_MANAGEMENT_NO_PARAMS,
  SERVICE_CONTENT_MANAGEMENT_WITH_PARAMS,
} from '../../constants/EntityName';
import { toastError, toastSuccess } from '../../service/Toast';
import Accordian from '../../components/Accordian/Accordian';
import {
  AboutMainSectionFields,
  ContentBlocksSectionFields,
} from '../../components/ContentManagement/AboutSectionFields';
import {
  BenefitsSectionFields,
  FeesSectionFields,
  KeyRulesSectionFields,
  RelatedServicesSectionFields,
} from '../../components/ContentManagement/PackageExtrasSectionFields';
import {
  PopularServicesSectionFields,
  TimelineSectionFields,
  RelatedArticlesSectionFields,
} from '../../components/ContentManagement/TimelineAndArticlesSectionFields';
import {
  buildContentManagementPayload,
  parseAboutFormFromRecord,
  parsePackageExtrasFormFromRecord,
} from '../../components/ContentManagement/contentPayload';
import { loadExistingTimeline } from '../../components/ContentManagement/syncExistingTimeline';
import ConfirmationModal from '../../components/ConfirmationModal';
import { InvertFillButton } from '../../components/InvertFillButton';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';

import './style.scss';

type Mode = 'serviceNoParams' | 'serviceWithParams' | 'package' | 'packageNoParams';
type ParameterKey = 'applyingFrom' | 'destination' | 'citizenship';

interface ParameterCombination {
  applyingFrom: string | null;
  destination: string | null;
  citizenship: string | null;
}

interface ManagementRecord {
  id: string;
  service?: any;
  package?: any;
  headingTitle?: string;
  headingDescription?: string;
  applyingFrom?: string;
  destination?: string;
  citizenship?: string;
  steps?: { sequence: number; description: string }[];
  stepsHeading?: string;
  stepsSubtitle?: string;
  process?: string;
  aboutBlocks?: {
    sequence: number;
    type: 'paragraph' | 'qa';
    html?: string;
    question?: string;
    answer?: string;
    answerFormat?: 'html' | 'checklist';
    checklistItems?: string[];
  }[];
  aboutHeading?: {
    text?: string;
    highlight?: string;
    subtitle?: string;
    level?: number;
  };
  keyRulesSection?: Record<string, unknown>;
  feesSection?: Record<string, unknown>;
  benefitsSection?: Record<string, unknown>;
  relatedServicesSection?: Record<string, unknown>;
  timelineName?: string | null;
  timelineTable?: string[][];
  faqs?: {
    sequence: number;
    question: string;
    answer: string;
    answerFormat?: 'text' | 'html' | 'checklist';
    checklistItems?: string[];
  }[];
  isActive?: boolean;
  groupedRecords?: ManagementRecord[];
}

interface ContentManagementPageProps {
  mode: Mode;
}

const parameterKeys: ParameterKey[] = ['applyingFrom', 'destination', 'citizenship'];

const parameterLabels: Record<string, string> = {
  applyingFrom: 'Applying From',
  destination: 'Destination',
  citizenship: 'Citizenship',
};

const endpointByMode = {
  serviceNoParams: SERVICE_CONTENT_MANAGEMENT,
  serviceWithParams: SERVICE_CONTENT_MANAGEMENT,
  package: PACKAGE_CONTENT_MANAGEMENT,
  packageNoParams: PACKAGE_CONTENT_MANAGEMENT,
};

const listEndpointByMode = {
  serviceNoParams: SERVICE_CONTENT_MANAGEMENT_NO_PARAMS,
  serviceWithParams: SERVICE_CONTENT_MANAGEMENT_WITH_PARAMS,
  package: PACKAGE_CONTENT_MANAGEMENT,
  packageNoParams: PACKAGE_CONTENT_MANAGEMENT,
};

const ownerFieldByMode = {
  serviceNoParams: 'service',
  serviceWithParams: 'service',
  package: 'package',
  packageNoParams: 'package',
};

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: ['small', false, 'large', 'huge'] }, { color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
    ['link'],
    ['clean'],
    ['code-block'],
  ],
};

const standardFormItemProps = {
  labelCol: { span: 4 },
  wrapperCol: { span: 16 },
  labelAlign: 'left' as const,
};

const wideFormItemProps = {
  labelCol: { span: 4 },
  wrapperCol: { span: 16 },
  labelAlign: 'left' as const,
};

const inputStyle = { width: '100%' };
const textAreaStyle = { width: '100%', minHeight: 100 };

const getResponseList = (response: any) => {
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  return [];
};

const getResponseTotalCount = (response: any): number => {
  const data = response?.data?.data;
  const total = data?.totalCount ?? response?.data?.totalCount;
  return typeof total === 'number' ? total : parseInt(total, 10) || 0;
};

const getResponseItem = (response: any) => response?.data?.data || response?.data || null;

const normalizeCountryOptions = (value: any) => {
  if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean);
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map(item => String(item).trim()).filter(Boolean);
    }
  } catch {
    // TypeORM simple-array values arrive as comma-separated strings in some responses.
  }

  return trimmed
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const normalizeParameterValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || null;
};

const normalizeParameterList = (value?: string | string[] | null) => {
  const normalizedValues = Array.isArray(value)
    ? value.map(item => `${item ?? ''}`.trim()).filter(Boolean)
    : value
      ? normalizeCountryOptions(value)
      : [];

  return Array.from(new Set(normalizedValues));
};

const formatParameterValue = (value?: string | string[] | null) => {
  const values = normalizeParameterList(value);
  return values.length ? JSON.stringify(values) : null;
};

const buildParameterCombinations = (values: any): ParameterCombination[] => {
  const normalizedParameters = parameterKeys.reduce(
    (result, key) => ({
      ...result,
      [key]: formatParameterValue(values[key]),
    }),
    {} as Record<ParameterKey, string | null>
  );

  return [normalizedParameters];
};

const getUniqueParameterValues = (records: ManagementRecord[], key: ParameterKey) =>
  Array.from(
    new Set(records.flatMap(record => normalizeParameterList(record[key])).filter(Boolean))
  );

const parameterValuesOverlap = (existingValue?: string | null, nextValue?: string | null) => {
  const existingValues = normalizeParameterList(existingValue);
  const nextValues = normalizeParameterList(nextValue);

  if (!existingValues.length && !nextValues.length) return true;
  if (!existingValues.length || !nextValues.length) return false;

  return existingValues.some(value => nextValues.includes(value));
};

const hasOverlappingParameterCombination = (
  record: ManagementRecord,
  combination: ParameterCombination
) => parameterKeys.every(key => parameterValuesOverlap(record[key], combination[key]));

const displayParameterValue = (value?: string | string[] | null) => {
  const values = normalizeParameterList(value);
  return values.length ? values.join(', ') : '-';
};

const recordHasParameters = (record: ManagementRecord) =>
  parameterKeys.some(key => normalizeParameterList(record[key]).length > 0);

// ---------------------------------------------------------------------------
// Self-contained form for editing / duplicating one record — owns all its state
// ---------------------------------------------------------------------------
interface RecordEditFormProps {
  mode: Mode;
  /** The record to edit (or the source when isDuplicate=true). */
  initialRecord: ManagementRecord;
  isDuplicate: boolean;
  /** Up-to-date full records list (used for duplicate-param check). */
  allRecordsRef: React.RefObject<ManagementRecord[]>;
  onSaved: () => void;
  onClose: () => void;
}

const RecordEditForm = ({
  mode,
  initialRecord,
  isDuplicate,
  allRecordsRef,
  onSaved,
  onClose,
}: RecordEditFormProps) => {
  const [form] = Form.useForm();
  const isPackageMode = mode === 'package' || mode === 'packageNoParams';
  const isPackageWithParams = mode === 'package';
  const isServiceWithParams = mode === 'serviceWithParams';
  const requiresParameters = isPackageWithParams || isServiceWithParams;
  const endpoint = endpointByMode[mode];
  const ownerField = ownerFieldByMode[mode];
  const headerName = isPackageMode
    ? 'Packages Content management'
    : requiresParameters
      ? 'Service content management (With parameter)'
      : 'Service content management (Service only)';

  const [services, setServices] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>();
  const [selectedOwner, setSelectedOwner] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const initialised = useRef(false);

  // ---------- load lookups + pre-fill ----------
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      // Load services list
      const svcRes = await callApi(APPLICATION_FORM_SERVICES, 'GET', null, { page: 1, limit: 100 });
      const svcList = getResponseList(svcRes);
      setServices(svcList);

      const owner = isPackageMode ? initialRecord.package : initialRecord.service;

      if (isPackageMode) {
        const serviceId = owner?.service?.id;
        setSelectedServiceId(serviceId);

        if (serviceId) {
          const pkgRes = await callApi(APPLICATION_FORM_PACKAGES, 'GET', null, {
            page: 1,
            limit: 100,
            service: serviceId,
            validOnly: true,
          });
          const pkgList = [...getResponseList(pkgRes)].reverse();
          setPackages(pkgList);
        }

        const pkgDetail = owner?.id
          ? getResponseItem(await callApi(`${APPLICATION_FORM_PACKAGES}/${owner.id}`, 'GET'))
          : null;
        setSelectedOwner(pkgDetail?.id ? pkgDetail : owner);
      } else {
        const svcDetail = owner?.id
          ? getResponseItem(await callApi(`${APPLICATION_FORM_SERVICES}/${owner.id}`, 'GET'))
          : null;
        setSelectedOwner(svcDetail?.id ? svcDetail : owner);
      }

      let recordForForm = initialRecord;
      if (!isDuplicate && initialRecord.id) {
        const fetched = getResponseItem(await callApi(`${endpoint}/${initialRecord.id}`, 'GET'));
        if (fetched?.id) {
          recordForForm = {
            ...initialRecord,
            ...fetched,
            package: fetched.package || initialRecord.package,
            service: fetched.service || initialRecord.service,
          };
        }
      }

      const groupedRecords = recordForForm.groupedRecords || [recordForForm];

      form.setFieldsValue({
        packageService: owner?.service?.id,
        [ownerField]: owner?.id,
        headingTitle: isDuplicate ? undefined : recordForForm.headingTitle || '',
        headingDescription: isDuplicate ? undefined : recordForForm.headingDescription || '',
        stepsHeading: isDuplicate ? undefined : recordForForm.stepsHeading || '',
        stepsSubtitle: isDuplicate ? undefined : recordForForm.stepsSubtitle || '',
        // duplicates start with blank params; edits pre-fill them
        applyingFrom: isDuplicate
          ? undefined
          : getUniqueParameterValues(groupedRecords, 'applyingFrom'),
        destination: isDuplicate
          ? undefined
          : getUniqueParameterValues(groupedRecords, 'destination'),
        citizenship: isDuplicate
          ? undefined
          : getUniqueParameterValues(groupedRecords, 'citizenship'),
        steps: recordForForm.steps?.length
          ? recordForForm.steps
          : [{ sequence: 1, description: '' }],
        process: recordForForm.process || '',
        ...parseAboutFormFromRecord(recordForForm),
        ...parsePackageExtrasFormFromRecord(recordForForm),
        timelineName: isDuplicate ? '' : recordForForm.timelineName || '',
        timelineTable:
          !isDuplicate && Array.isArray(recordForForm.timelineTable)
            ? recordForForm.timelineTable
            : [
                ['', ''],
                ['', ''],
              ],
        faqs: recordForForm.faqs?.length
          ? recordForForm.faqs
          : [{ sequence: 1, question: '', answer: '' }],
        isActive: recordForForm.isActive !== false,
      });

      // Prefill Timeline accordion from existing Package/Service Timeline APIs.
      if (
        !isDuplicate &&
        (!Array.isArray(recordForForm.timelineTable) || !recordForForm.timelineTable.length)
      ) {
        const legacyTimeline = await loadExistingTimeline({
          isPackageMode,
          ownerId: owner?.id,
        });
        if (legacyTimeline) {
          form.setFieldsValue(legacyTimeline);
        }
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- options ----------
  const ownerOptions = useMemo(() => {
    const source = isPackageMode ? packages : services;
    return [...source].reverse().map(item => ({
      label: isPackageMode
        ? `${item.name}${item.service?.name ? ` (${item.service.name})` : ''}`
        : item.name,
      value: item.id,
    }));
  }, [isPackageMode, packages, services]);

  const packageServiceOptions = useMemo(
    () => [...services].reverse().map(s => ({ label: s.name, value: s.id })),
    [services]
  );

  const selectedParameters: string[] =
    selectedOwner?.service?.parameters || selectedOwner?.parameters || [];

  const countryOptionsByParameter = useMemo(() => {
    const getOptions = (parameter: string) =>
      Array.from(new Set<string>(normalizeCountryOptions(selectedOwner?.[parameter]))).map(
        (v: string) => ({
          label: v,
          value: v,
        })
      );
    return {
      applyingFrom: getOptions('applyingFrom'),
      destination: getOptions('destination'),
      citizenship: getOptions('citizenship'),
    };
  }, [selectedOwner]);

  // ---------- service/package change ----------
  const handlePackageServiceChange = async (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setSelectedOwner(undefined);
    form.setFieldsValue({
      package: undefined,
      applyingFrom: undefined,
      destination: undefined,
      citizenship: undefined,
    });

    const pkgRes = await callApi(APPLICATION_FORM_PACKAGES, 'GET', null, {
      page: 1,
      limit: 100,
      service: serviceId,
      validOnly: true,
    });
    setPackages([...getResponseList(pkgRes)].reverse());
  };

  const handleOwnerChange = async (id: string) => {
    let owner = (isPackageMode ? packages : services).find(item => item.id === id);
    if (isPackageMode) {
      const d = getResponseItem(await callApi(`${APPLICATION_FORM_PACKAGES}/${id}`, 'GET'));
      owner = d?.id ? d : owner;
    } else {
      const d = getResponseItem(await callApi(`${APPLICATION_FORM_SERVICES}/${id}`, 'GET'));
      owner = d?.id ? d : owner;
    }
    setSelectedOwner(owner);
    form.setFieldsValue({
      applyingFrom: undefined,
      destination: undefined,
      citizenship: undefined,
    });

    const legacyTimeline = await loadExistingTimeline({
      isPackageMode,
      ownerId: owner?.id,
    });
    if (legacyTimeline) {
      form.setFieldsValue(legacyTimeline);
    } else {
      form.setFieldsValue({
        timelineName: '',
        timelineTable: [
          ['', ''],
          ['', ''],
        ],
      });
    }
  };

  // ---------- submit ----------
  const handleSubmit = async (values: any) => {
    const allRecords = allRecordsRef.current || [];
    const selectedOwnerId = values[ownerField];

    const editableRecords = isDuplicate ? [] : initialRecord.groupedRecords || [initialRecord];
    const editableRecordIds = new Set(editableRecords.map(r => r.id));
    const skipIds = new Set<string>(editableRecordIds);

    // Package content: parameters are required (mode === 'package').
    if (isPackageMode) {
      if (requiresParameters) {
        const missingRequiredParameter = selectedParameters.some(
          parameter => !normalizeParameterList(values[parameter]).length
        );
        if (missingRequiredParameter) {
          toastError('Please select all required parameters');
          return;
        }

        const parameterCombinations = buildParameterCombinations(values);
        const duplicateCombination = parameterCombinations.find(combination =>
          allRecords.some(record => {
            const currentPackageId = record?.package?.id;
            if (!selectedOwnerId || currentPackageId !== selectedOwnerId) return false;
            if (skipIds.has(record.id)) return false;
            return hasOverlappingParameterCombination(record, combination);
          })
        );

        if (!isDuplicate && duplicateCombination) {
          toastError('Duplicate package SPF entry exists for the same parameters');
          return;
        }

        setSubmitting(true);
        const contentPayload = buildContentManagementPayload(values, form, {
          includePopularServices: false,
          includeRelatedArticles: false,
        });
        const buildPayload = (combination: ParameterCombination) => ({
          [ownerField]: { id: selectedOwnerId },
          applyingFrom: combination.applyingFrom,
          destination: combination.destination,
          citizenship: combination.citizenship,
          ...contentPayload,
        });

        try {
          const requests = isDuplicate
            ? parameterCombinations.map(combination =>
                callApi(endpoint, 'POST', buildPayload(combination))
              )
            : [
                ...parameterCombinations.map((combination, index) => {
                  const existingRecord = editableRecords[index];
                  return existingRecord
                    ? callApi(
                        `${endpoint}/${existingRecord.id}`,
                        'PATCH',
                        buildPayload(combination)
                      )
                    : callApi(endpoint, 'POST', buildPayload(combination));
                }),
                ...editableRecords
                  .slice(parameterCombinations.length)
                  .map(record => callApi(`${endpoint}/${record.id}`, 'DELETE')),
              ];

          const responses = await Promise.all(requests);
          const failedResponse = responses.find(response => !response?.status);

          if (!failedResponse) {
            toastSuccess(`${headerName} ${isDuplicate ? 'duplicated' : 'updated'} successfully`);
            onSaved();
          } else {
            toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
          }
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // packageNoParams fallback (legacy route)
      const duplicateEntry = allRecords.find(record => {
        const currentOwnerId = record?.package?.id;
        if (!selectedOwnerId || currentOwnerId !== selectedOwnerId) return false;
        if (skipIds.has(record.id)) return false;
        return !recordHasParameters(record);
      });

      if (!isDuplicate && duplicateEntry) {
        toastError('Duplicate package SPF entry already exists for this package');
        return;
      }

      setSubmitting(true);
      const contentPayload = buildContentManagementPayload(values, form, {
        includePopularServices: false,
        includeRelatedArticles: false,
      });
      const buildPayload = () => ({
        [ownerField]: { id: selectedOwnerId },
        applyingFrom: null,
        destination: null,
        citizenship: null,
        ...contentPayload,
      });

      try {
        const requests = isDuplicate
          ? [callApi(endpoint, 'POST', buildPayload())]
          : editableRecords.map(record =>
              callApi(`${endpoint}/${record.id}`, 'PATCH', buildPayload())
            );

        const responses = await Promise.all(requests);
        const failedResponse = responses.find(response => !response?.status);

        if (!failedResponse) {
          toastSuccess(`${headerName} ${isDuplicate ? 'duplicated' : 'updated'} successfully`);
          onSaved();
        } else {
          toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Service only: one content row per service, no parameters.
    if (!requiresParameters) {
      const duplicateEntry = allRecords.find(record => {
        const currentServiceId = record?.service?.id;
        if (!selectedOwnerId || currentServiceId !== selectedOwnerId) return false;
        if (skipIds.has(record.id)) return false;
        return !recordHasParameters(record);
      });

      if (!isDuplicate && duplicateEntry) {
        toastError('Duplicate service SPF entry already exists for this service');
        return;
      }

      setSubmitting(true);
      const contentPayload = buildContentManagementPayload(values, form, {
        includePopularServices: true,
        includeRelatedArticles: true,
      });
      const buildPayload = () => ({
        [ownerField]: { id: selectedOwnerId },
        applyingFrom: null,
        destination: null,
        citizenship: null,
        ...contentPayload,
      });

      try {
        const requests = isDuplicate
          ? [callApi(endpoint, 'POST', buildPayload())]
          : editableRecords.map(record =>
              callApi(`${endpoint}/${record.id}`, 'PATCH', buildPayload())
            );

        const responses = await Promise.all(requests);
        const failedResponse = responses.find(response => !response?.status);

        if (!failedResponse) {
          toastSuccess(`${headerName} ${isDuplicate ? 'duplicated' : 'updated'} successfully`);
          onSaved();
        } else {
          toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Service with parameter: parameters required; allow multiple rows by param combo.
    const parameterCombinations = buildParameterCombinations(values);
    const missingRequiredParameter = selectedParameters.some(
      parameter => !normalizeParameterList(values[parameter]).length
    );
    if (missingRequiredParameter) {
      toastError('Please select all required parameters');
      return;
    }

    const duplicateCombination = parameterCombinations.find(combination =>
      allRecords.some(record => {
        const currentServiceId = record?.service?.id;
        if (!selectedOwnerId || currentServiceId !== selectedOwnerId) return false;
        if (skipIds.has(record.id)) return false;
        if (!recordHasParameters(record)) return false;
        return hasOverlappingParameterCombination(record, combination);
      })
    );

    if (!isDuplicate && duplicateCombination) {
      toastError('Duplicate service SPF entry exists for the same parameters');
      return;
    }

    setSubmitting(true);
    const contentPayload = buildContentManagementPayload(values, form, {
      includePopularServices: false,
      includeRelatedArticles: false,
    });
    const buildPayload = (combination: ParameterCombination) => ({
      [ownerField]: { id: selectedOwnerId },
      applyingFrom: combination.applyingFrom,
      destination: combination.destination,
      citizenship: combination.citizenship,
      ...contentPayload,
    });

    try {
      const requests = isDuplicate
        ? parameterCombinations.map(combination =>
            callApi(endpoint, 'POST', buildPayload(combination))
          )
        : [
            ...parameterCombinations.map((combination, index) => {
              const existingRecord = editableRecords[index];
              return existingRecord
                ? callApi(`${endpoint}/${existingRecord.id}`, 'PATCH', buildPayload(combination))
                : callApi(endpoint, 'POST', buildPayload(combination));
            }),
            ...editableRecords
              .slice(parameterCombinations.length)
              .map(record => callApi(`${endpoint}/${record.id}`, 'DELETE')),
          ];

      const responses = await Promise.all(requests);
      const failedResponse = responses.find(response => !response?.status);

      if (!failedResponse) {
        toastSuccess(`${headerName} ${isDuplicate ? 'duplicated' : 'updated'} successfully`);
        onSaved();
      } else {
        toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- delete ----------
  const handleDelete = async () => {
    const recordsToDelete = initialRecord.groupedRecords || [initialRecord];
    setDeletingId(initialRecord.id);
    const responses = await Promise.all(
      recordsToDelete.map(item => callApi(`${endpoint}/${item.id}`, 'DELETE'))
    );
    const failedResponse = responses.find(response => !response?.status);
    setDeletingId(undefined);

    if (!failedResponse) {
      toastSuccess(`${headerName} deleted successfully`);
      onSaved();
    } else {
      toastError(failedResponse?.data?.message || `Unable to delete ${headerName}`);
    }
    setDeleteModalOpen(false);
  };

  // ---------- render ----------
  return (
    <div className="abstract-form">
      <div className="container1">
        <div className="form-container">
          <Form
            form={form}
            layout="horizontal"
            onFinish={handleSubmit}
            initialValues={{
              isActive: true,
              headingTitle: '',
              headingDescription: '',
              stepsHeading: '',
              stepsSubtitle: '',
              steps: [{ sequence: 1, description: '' }],
              faqs: [{ sequence: 1, question: '', answer: '' }],
              aboutDescription: '',
              aboutContentBlocks: [],
              aboutHeading: {
                text: '',
                highlight: '',
                subtitle: '',
                level: 2,
              },
              process: '',
              ...parsePackageExtrasFormFromRecord(),
            }}
          >
            <Accordian title={isPackageMode ? 'Package Information' : 'Service Information'}>
              {isPackageMode && (
                <Form.Item
                  name="packageService"
                  label="Service"
                  {...standardFormItemProps}
                  rules={[{ required: true, message: 'Service is required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={inputStyle}
                    options={packageServiceOptions}
                    placeholder="Select service"
                    onChange={handlePackageServiceChange}
                    dropdownClassName="select-checkbox-dropdown"
                  />
                </Form.Item>
              )}

              <Form.Item
                name={ownerField}
                label={isPackageMode ? 'Package' : 'Service'}
                {...standardFormItemProps}
                rules={[
                  {
                    required: true,
                    message: `${isPackageMode ? 'Package' : 'Service'} is required`,
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={inputStyle}
                  options={ownerOptions}
                  placeholder={
                    isPackageMode ? 'Select package after selecting service' : 'Select service'
                  }
                  onChange={handleOwnerChange}
                  disabled={isPackageMode && !selectedServiceId}
                  dropdownClassName="select-checkbox-dropdown"
                />
              </Form.Item>

              {requiresParameters &&
                selectedParameters.map(parameter => (
                  <Form.Item
                    key={parameter}
                    name={parameter}
                    label={parameterLabels[parameter] || parameter}
                    {...standardFormItemProps}
                    colon={false}
                    rules={[
                      {
                        required: true,
                        message: `${parameterLabels[parameter] || parameter} is required`,
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={inputStyle}
                      options={(countryOptionsByParameter as any)[parameter] || []}
                      placeholder={`Select one or more ${parameterLabels[parameter] || parameter}`}
                      dropdownClassName="select-checkbox-dropdown"
                    />
                  </Form.Item>
                ))}
            </Accordian>

            <Accordian title="Heading">
              <Form.Item
                name="headingTitle"
                label="Heading Title"
                {...standardFormItemProps}
                extra="Page heading on the frontend. Leave blank to use the service name."
              >
                <Input placeholder="e.g. OCI Card Application" style={inputStyle} maxLength={255} />
              </Form.Item>

              <Form.Item
                name="headingDescription"
                label="Social Proof Text"
                {...standardFormItemProps}
                extra="Text shown next to the avatar icons below the heading (e.g. 46,500+ Travel Documents processed by Documitra)"
              >
                <Input.TextArea
                  placeholder="e.g. 46,500+ Travel Documents processed by Documitra"
                  style={inputStyle}
                  rows={2}
                  maxLength={500}
                />
              </Form.Item>
            </Accordian>

            <Accordian title="Steps">
              <Form.Item
                name="stepsHeading"
                label="Title"
                {...standardFormItemProps}
                extra="Heading shown above the steps section on the frontend (e.g. Application Steps - When You Choose Us)"
              >
                <Input
                  placeholder="e.g. Application Steps - When You Choose Us"
                  style={inputStyle}
                  maxLength={255}
                />
              </Form.Item>

              <Form.Item
                name="stepsSubtitle"
                label="Description"
                {...standardFormItemProps}
                extra="Subtitle shown below the steps heading on the frontend"
              >
                <Input.TextArea
                  placeholder="e.g. Follow the process in a clear sequence."
                  style={inputStyle}
                  rows={2}
                  maxLength={500}
                />
              </Form.Item>

              <Form.List name="steps">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        key={field.key}
                        label={`Step ${index + 1}`}
                        {...wideFormItemProps}
                        required
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'description']}
                            rules={[{ required: true, message: 'Step description required' }]}
                            style={{ marginBottom: 0, width: '100%', padding: 0, border: 'none' }}
                          >
                            <Input.TextArea
                              rows={2}
                              placeholder="Step description"
                              style={textAreaStyle}
                            />
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button danger onClick={() => remove(field.name)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </Form.Item>
                    ))}
                    <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                      <Button
                        type="dashed"
                        onClick={() => add({ sequence: fields.length + 1, description: '' })}
                        style={{ color: '#637f92', fontWeight: 600 }}
                      >
                        + Add Step
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Accordian>

            <Accordian title="About / Guide Content">
              <AboutMainSectionFields />
            </Accordian>

            <Accordian title="Content Blocks">
              <ContentBlocksSectionFields />
            </Accordian>

            <Accordian title="Before You Apply – Key Requirements">
              <KeyRulesSectionFields />
            </Accordian>

            <Accordian title="Benefits of the Service (Optional)">
              <BenefitsSectionFields />
            </Accordian>

            <Accordian title="Service Fees (Optional)">
              <FeesSectionFields />
            </Accordian>

            <Accordian title="Related Services">
              <RelatedServicesSectionFields />
            </Accordian>

            {mode === 'serviceNoParams' && (
              <Accordian title="Popular Services">
                <PopularServicesSectionFields />
              </Accordian>
            )}

            <Accordian title="FAQs">
              <Form.List name="faqs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        key={field.key}
                        label={`FAQ ${index + 1}`}
                        {...wideFormItemProps}
                        required
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            width: '100%',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'question']}
                              rules={[{ required: true, message: 'FAQ title required' }]}
                              style={{
                                flex: 1,
                                marginBottom: 0,
                                padding: 0,
                                border: 'none',
                              }}
                            >
                              <Input placeholder="FAQ Question" />
                            </Form.Item>
                            {fields.length > 1 && (
                              <Button danger onClick={() => remove(field.name)}>
                                Remove
                              </Button>
                            )}
                          </div>
                          <Form.Item
                            {...field}
                            name={[field.name, 'answer']}
                            rules={[{ required: true, message: 'FAQ answer required' }]}
                            style={{ marginBottom: 0, padding: 0, border: 'none' }}
                          >
                            <Input.TextArea
                              rows={3}
                              placeholder="FAQ Answer"
                              style={textAreaStyle}
                            />
                          </Form.Item>
                        </div>
                      </Form.Item>
                    ))}
                    <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({ sequence: fields.length + 1, question: '', answer: '' })
                        }
                        style={{ color: '#637f92', fontWeight: 600 }}
                      >
                        + Add FAQ
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Accordian>

            <Accordian title="Timeline">
              <TimelineSectionFields />
            </Accordian>

            {mode === 'serviceNoParams' && (
              <Accordian title="Related Articles">
                <RelatedArticlesSectionFields />
              </Accordian>
            )}
          </Form>
        </div>
      </div>

      <div className="btn-container">
        <div className="publish-btn-container">
          <div className="publish-btn">
            <InvertFillButton
              variant={isDuplicate ? 'white' : 'blue'}
              size="md"
              fontWeight="semibold"
              disabled={submitting}
              onClick={() => form.submit()}
            >
              {isDuplicate ? 'Create Duplicate' : 'Update'}
            </InvertFillButton>
          </div>
          {!isDuplicate && (
            <InvertFillButton
              variant="red"
              size="md"
              fontWeight="semibold"
              disabled={deletingId === initialRecord.id}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </InvertFillButton>
          )}
          <InvertFillButton variant="white" size="md" fontWeight="semibold" onClick={onClose}>
            Cancel
          </InvertFillButton>
        </div>
      </div>

      <ConfirmationModal
        open={deleteModalOpen}
        message="Are you sure you want to delete?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        onClose={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const ContentManagementPage = ({ mode }: ContentManagementPageProps) => {
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState<string>('0');
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [records, setRecords] = useState<ManagementRecord[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [countryFilterOptions, setCountryFilterOptions] = useState<
    Array<{ id: string; value: string; name: string }>
  >([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>();
  const [selectedOwner, setSelectedOwner] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingRecordId, setDeletingRecordId] = useState<string>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [filterQuery, setFilterQuery] = useState<any>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [totalCount, setTotalCount] = useState<number>(0);

  // Tabs for editing/duplicating individual records
  type TabItem = { key: string; tabTitle: string; content: React.ReactNode };
  const [contentTabs, setContentTabs] = useState<TabItem[]>([]);

  const isPackageMode = mode === 'package' || mode === 'packageNoParams';
  const isPackageWithParams = mode === 'package';
  const isServiceWithParams = mode === 'serviceWithParams';
  const requiresParameters = isPackageWithParams || isServiceWithParams;
  const endpoint = endpointByMode[mode];
  const listEndpoint = listEndpointByMode[mode];
  const ownerField = ownerFieldByMode[mode];
  const headerName = isPackageMode
    ? 'Packages Content management'
    : requiresParameters
      ? 'Service content management (With parameter)'
      : 'Service content management (Service only)';
  const addName = isPackageMode
    ? 'Package Content'
    : requiresParameters
      ? 'Service Content (With parameter)'
      : 'Service Content (Service only)';

  // Keep a live ref to records so RecordEditForm can read the latest list
  const allRecordsRef = useRef<ManagementRecord[]>([]);
  useEffect(() => {
    allRecordsRef.current = records;
  }, [records]);

  // ---- shared "create new" form state (tab 'a') ----
  const duplicatingRecord = undefined as ManagementRecord | undefined; // create tab never duplicates
  const editingRecord = undefined as ManagementRecord | undefined;

  const ownerOptions = useMemo(() => {
    const source = isPackageMode ? packages : services;
    return [...source].reverse().map(item => ({
      label: isPackageMode
        ? `${item.name}${item.service?.name ? ` (${item.service.name})` : ''}`
        : item.name,
      value: item.id,
    }));
  }, [isPackageMode, packages, services]);

  const packageServiceOptions = useMemo(
    () =>
      [...services].reverse().map(service => ({
        label: service.name,
        value: service.id,
      })),
    [services]
  );

  const selectedParameters: string[] =
    selectedOwner?.service?.parameters || selectedOwner?.parameters || [];

  const prefetchedFilterOptions = useMemo(
    () => ({
      services: [...services].reverse().map(service => ({
        id: service.id,
        value: service.id,
        name: service.name,
      })),
      countries: countryFilterOptions,
    }),
    [services, countryFilterOptions]
  );

  const tableRecords = useMemo(() => {
    const filteredRecords =
      mode === 'serviceNoParams'
        ? records.filter(record => !recordHasParameters(record))
        : mode === 'serviceWithParams'
          ? records.filter(record => recordHasParameters(record))
          : records;

    return filteredRecords.map(record => ({
      ...record,
      groupedRecords: [record],
      applyingFrom: displayParameterValue(record.applyingFrom),
      destination: displayParameterValue(record.destination),
      citizenship: displayParameterValue(record.citizenship),
    }));
  }, [mode, records]);

  const countryOptionsByParameter = useMemo(() => {
    const getOptions = (parameter: string) => {
      const values = normalizeCountryOptions(selectedOwner?.[parameter]);

      return Array.from(new Set<string>(values)).map((value: string) => ({
        label: value,
        value,
      }));
    };

    return {
      applyingFrom: getOptions('applyingFrom'),
      destination: getOptions('destination'),
      citizenship: getOptions('citizenship'),
    };
  }, [selectedOwner]);

  const getData = async (paginationEvent: { current: number; pageSize: number } = pagination) => {
    const queryParams: Record<string, string | number> = {
      _t: Date.now(),
      page: paginationEvent.current,
      limit: paginationEvent.pageSize,
    };

    if (searchQuery) {
      queryParams.search = searchQuery;
    }

    if (filterQuery && typeof filterQuery === 'object') {
      Object.entries(filterQuery).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          queryParams[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
        }
      });
    }

    setLoading(true);
    try {
      const response = await callApi(listEndpoint, 'GET', null, queryParams);
      setRecords(getResponseList(response));
      setTotalCount(getResponseTotalCount(response));
    } catch {
      setRecords([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = async (event: { current?: number; pageSize?: number }) => {
    const newPagination = {
      current: event.current || 1,
      pageSize: event.pageSize || 10,
    };
    setPagination(newPagination);
    await getData(newPagination);
  };

  const loadLookups = async () => {
    const [serviceResponse, countryResponse] = await Promise.all([
      callApi(APPLICATION_FORM_SERVICES, 'GET', null, {
        page: 1,
        limit: 100,
      }),
      callApi('admin/countries', 'GET'),
    ]);

    setServices(getResponseList(serviceResponse));

    const countries = countryResponse?.data?.data || [];
    setCountryFilterOptions(
      countries.map((country: string) => ({
        id: country,
        value: country,
        name: country,
      }))
    );

    if (isPackageMode) setPackages([]);
  };

  const loadPackagesByService = async (serviceId: string) => {
    if (!serviceId) {
      setPackages([]);
      return [];
    }

    const packageResponse = await callApi(APPLICATION_FORM_PACKAGES, 'GET', null, {
      page: 1,
      limit: 100,
      service: serviceId,
      validOnly: true,
    });
    const packageList = [...getResponseList(packageResponse)].reverse();
    setPackages(packageList);
    return packageList;
  };

  const loadPackageDetails = async (packageId?: string) => {
    if (!packageId) return undefined;

    const packageResponse = await callApi(`${APPLICATION_FORM_PACKAGES}/${packageId}`, 'GET');
    return getResponseItem(packageResponse);
  };

  const loadServiceDetails = async (serviceId?: string) => {
    if (!serviceId) return undefined;

    const serviceResponse = await callApi(`${APPLICATION_FORM_SERVICES}/${serviceId}`, 'GET');
    return getResponseItem(serviceResponse);
  };

  useEffect(() => {
    const init = async () => {
      await loadLookups();
      setPagination(prev => ({ ...prev, current: 1 }));
      await getData({ current: 1, pageSize: pagination.pageSize });
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, searchQuery, filterQuery]);

  const setDefaultFormValues = () => {
    form.setFieldsValue({
      isActive: true,
      headingTitle: '',
      headingDescription: '',
      stepsHeading: '',
      stepsSubtitle: '',
      steps: [{ sequence: 1, description: '' }],
      faqs: [{ sequence: 1, question: '', answer: '' }],
      aboutDescription: '',
      aboutContentBlocks: [],
      aboutHeading: {
        text: '',
        highlight: '',
        subtitle: '',
        level: 2,
      },
      process: '',
      ...parsePackageExtrasFormFromRecord(),
    });
  };

  const openCreateForm = () => {
    setSelectedServiceId(undefined);
    setSelectedOwner(undefined);
    if (isPackageMode) setPackages([]);
    form.resetFields();
    setDefaultFormValues();
    setShowFormComponent(true);
    setActiveKey('a');
  };

  const closeCreateForm = (shouldRefresh = false) => {
    setShowFormComponent(false);
    setSelectedServiceId(undefined);
    setSelectedOwner(undefined);
    if (isPackageMode) setPackages([]);
    setActiveKey('0');
    form.resetFields();
    if (shouldRefresh) getData(pagination);
  };

  const handlePackageServiceChange = async (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setSelectedOwner(undefined);
    form.setFieldsValue({
      package: undefined,
      applyingFrom: undefined,
      destination: undefined,
      citizenship: undefined,
    });
    await loadPackagesByService(serviceId);
  };

  const handleOwnerChange = async (id: string) => {
    let owner = (isPackageMode ? packages : services).find(item => item.id === id);
    if (isPackageMode) {
      const packageDetails = await loadPackageDetails(id);
      owner = packageDetails?.id ? packageDetails : owner;
    } else {
      const serviceDetails = await loadServiceDetails(id);
      owner = serviceDetails?.id ? serviceDetails : owner;
    }
    setSelectedOwner(owner);
    form.setFieldsValue({
      applyingFrom: undefined,
      destination: undefined,
      citizenship: undefined,
    });

    const legacyTimeline = await loadExistingTimeline({
      isPackageMode,
      ownerId: owner?.id,
    });
    if (legacyTimeline) {
      form.setFieldsValue(legacyTimeline);
    } else {
      form.setFieldsValue({
        timelineName: '',
        timelineTable: [
          ['', ''],
          ['', ''],
        ],
      });
    }
  };

  // ---- open record in its own tab ----
  const tabKeyFor = (record: ManagementRecord, isDuplicate: boolean) =>
    isDuplicate ? `dup-${record.id}` : `edit-${record.id}`;

  const openRecordTab = (record: ManagementRecord, isDuplicate: boolean) => {
    const key = tabKeyFor(record, isDuplicate);
    const existing = contentTabs.find(t => t.key === key);
    if (existing) {
      setActiveKey(key);
      return;
    }

    const owner = isPackageMode ? record.package : record.service;
    const ownerName = owner?.name || record.id;
    const tabTitle = isDuplicate ? `Duplicate: ${ownerName}` : ownerName;

    const onSaved = () => {
      setContentTabs(prev => prev.filter(t => t.key !== key));
      setActiveKey('0');
      getData(pagination);
    };
    const onClose = () => {
      setContentTabs(prev => prev.filter(t => t.key !== key));
      setActiveKey('0');
    };

    const tab: TabItem = {
      key,
      tabTitle,
      content: (
        <RecordEditForm
          key={key}
          mode={mode}
          initialRecord={record}
          isDuplicate={isDuplicate}
          allRecordsRef={allRecordsRef}
          onSaved={onSaved}
          onClose={onClose}
        />
      ),
    };

    setContentTabs(prev => [...prev, tab]);
    setActiveKey(key);
  };

  const handleEdit = (record: ManagementRecord) => openRecordTab(record, false);
  const handleDuplicate = (record: ManagementRecord) => openRecordTab(record, true);

  const handleDelete = async (record: ManagementRecord) => {
    const recordsToDelete = record.groupedRecords || [record];
    setDeletingRecordId(record.id);
    const responses = await Promise.all(
      recordsToDelete.map(item => callApi(`${endpoint}/${item.id}`, 'DELETE'))
    );
    const failedResponse = responses.find(response => !response?.status);
    setDeletingRecordId(undefined);

    if (!failedResponse) {
      toastSuccess(`${headerName} deleted successfully`);
      getData(pagination);
      return;
    }

    toastError(failedResponse?.data?.message || `Unable to delete ${headerName}`);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const columns: ColumnsType<ManagementRecord> = [
    ...(isPackageMode
      ? [
          {
            title: 'Service',
            dataIndex: 'package',
            render: (pkg: any) => pkg?.service?.name || '-',
          },
        ]
      : []),
    {
      title: isPackageMode ? 'Package' : 'Service',
      dataIndex: ownerField,
      render: (owner: any, record) =>
        owner?.name ? (
          <Button type="link" onClick={() => handleEdit(record)} style={{ padding: 0 }}>
            {owner.name}
          </Button>
        ) : (
          '-'
        ),
    },
    ...(requiresParameters
      ? [
          {
            title: 'Applying From',
            dataIndex: 'applyingFrom',
            render: (value?: string) => displayParameterValue(value),
          },
          {
            title: 'Citizenship',
            dataIndex: 'citizenship',
            render: (value?: string) => displayParameterValue(value),
          },
          {
            title: 'Destination',
            dataIndex: 'destination',
            render: (value?: string) => displayParameterValue(value),
          },
        ]
      : []),
    {
      title: 'Steps',
      dataIndex: 'steps',
      render: (steps?: any[]) => steps?.length || 0,
    },
    {
      title: 'FAQs',
      dataIndex: 'faqs',
      render: (faqs?: any[]) => faqs?.length || 0,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: (isActive?: boolean) => (isActive === false ? 'No' : 'Yes'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: ManagementRecord) => (
        <Button onClick={() => handleDuplicate(record)}>Make Duplicate</Button>
      ),
    },
  ];

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        closeCreateForm();
      } else {
        setContentTabs(prev => prev.filter(t => t.key !== targetKey));
        setActiveKey('0');
      }
    }
  };

  const handleTableChange = (nextPagination: any) => {
    onPageChange(nextPagination);
  };

  // ---- "create new" form (tab 'a') ----
  const handleCreateSubmit = async (values: any) => {
    const selectedOwnerId = values[ownerField];

    // Package content: parameters required when mode === 'package'.
    if (isPackageMode) {
      if (requiresParameters) {
        const missingRequiredParameter = selectedParameters.some(
          parameter => !normalizeParameterList(values[parameter]).length
        );
        if (missingRequiredParameter) {
          toastError('Please select all required parameters');
          return;
        }
      }

      const parameterCombinations = requiresParameters
        ? buildParameterCombinations(values)
        : [
            {
              applyingFrom: null as string | null,
              destination: null as string | null,
              citizenship: null as string | null,
            },
          ];

      const duplicateCombination = parameterCombinations.find(combination =>
        records.some(record => {
          const currentPackageId = record?.package?.id;
          if (!selectedOwnerId || currentPackageId !== selectedOwnerId) return false;
          if (requiresParameters) {
            return hasOverlappingParameterCombination(record, combination);
          }
          return !recordHasParameters(record);
        })
      );

      if (duplicateCombination) {
        toastError(
          requiresParameters
            ? 'Duplicate package SPF entry exists for the same parameters'
            : 'Duplicate package SPF entry already exists for this package'
        );
        return;
      }

      setSubmitting(true);
      const contentPayload = buildContentManagementPayload(values, form, {
        includePopularServices: false,
        includeRelatedArticles: false,
      });
      const buildPayload = (combination: ParameterCombination) => ({
        [ownerField]: { id: selectedOwnerId },
        applyingFrom: combination.applyingFrom,
        destination: combination.destination,
        citizenship: combination.citizenship,
        ...contentPayload,
      });

      try {
        const requests = parameterCombinations.map(combination =>
          callApi(endpoint, 'POST', buildPayload(combination))
        );
        const responses = await Promise.all(requests);
        const failedResponse = responses.find(response => !response?.status);

        if (!failedResponse) {
          const countSuffix =
            parameterCombinations.length > 1
              ? ` for ${parameterCombinations.length} parameter combination(s)`
              : '';
          toastSuccess(`${headerName} created successfully${countSuffix}`);
          closeCreateForm(true);
        } else {
          toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Service only: no parameters, one entry per service.
    if (!requiresParameters) {
      const duplicateEntry = records.some(record => {
        const currentServiceId = record?.service?.id;
        return (
          !!selectedOwnerId && currentServiceId === selectedOwnerId && !recordHasParameters(record)
        );
      });

      if (duplicateEntry) {
        toastError('Duplicate service SPF entry already exists for this service');
        return;
      }

      setSubmitting(true);
      const contentPayload = buildContentManagementPayload(values, form, {
        includePopularServices: true,
        includeRelatedArticles: true,
      });
      const payload = {
        [ownerField]: { id: selectedOwnerId },
        applyingFrom: null,
        destination: null,
        citizenship: null,
        ...contentPayload,
      };

      try {
        const response = await callApi(endpoint, 'POST', payload);
        if (response?.status) {
          toastSuccess(`${headerName} created successfully`);
          closeCreateForm(true);
        } else {
          toastError(response?.data?.message || `Unable to save ${headerName}`);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Service with parameter: parameters required.
    const missingRequiredParameter = selectedParameters.some(
      parameter => !normalizeParameterList(values[parameter]).length
    );
    if (missingRequiredParameter) {
      toastError('Please select all required parameters');
      return;
    }

    const parameterCombinations = buildParameterCombinations(values);
    const duplicateCombination = parameterCombinations.find(combination =>
      records.some(record => {
        const currentServiceId = record?.service?.id;
        if (!selectedOwnerId || currentServiceId !== selectedOwnerId) return false;
        if (!recordHasParameters(record)) return false;
        return hasOverlappingParameterCombination(record, combination);
      })
    );

    if (duplicateCombination) {
      toastError('Duplicate service SPF entry exists for the same parameters');
      return;
    }

    setSubmitting(true);
    const contentPayload = buildContentManagementPayload(values, form, {
      includePopularServices: false,
      includeRelatedArticles: false,
    });
    const buildPayload = (combination: ParameterCombination) => ({
      [ownerField]: { id: selectedOwnerId },
      applyingFrom: combination.applyingFrom,
      destination: combination.destination,
      citizenship: combination.citizenship,
      ...contentPayload,
    });

    try {
      const requests = parameterCombinations.map(combination =>
        callApi(endpoint, 'POST', buildPayload(combination))
      );

      const responses = await Promise.all(requests);
      const failedResponse = responses.find(response => !response?.status);

      if (!failedResponse) {
        const countSuffix =
          parameterCombinations.length > 1
            ? ` for ${parameterCombinations.length} parameter combination(s)`
            : '';
        toastSuccess(`${headerName} created successfully${countSuffix}`);
        closeCreateForm(true);
      } else {
        toastError(failedResponse?.data?.message || `Unable to save ${headerName}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formComponent = (
    <div className="abstract-form">
      <div className="container1">
        <div className="form-container">
          <Form
            form={form}
            layout="horizontal"
            onFinish={handleCreateSubmit}
            initialValues={{
              isActive: true,
              headingTitle: '',
              headingDescription: '',
              stepsHeading: '',
              stepsSubtitle: '',
              steps: [{ sequence: 1, description: '' }],
              faqs: [{ sequence: 1, question: '', answer: '' }],
              aboutDescription: '',
              aboutContentBlocks: [],
              aboutHeading: {
                text: '',
                highlight: '',
                subtitle: '',
                level: 2,
              },
              process: '',
              ...parsePackageExtrasFormFromRecord(),
            }}
          >
            <Accordian title={isPackageMode ? 'Package Information' : 'Service Information'}>
              {isPackageMode && (
                <Form.Item
                  name="packageService"
                  label="Service"
                  {...standardFormItemProps}
                  rules={[{ required: true, message: 'Service is required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={inputStyle}
                    options={packageServiceOptions}
                    placeholder="Select service"
                    onChange={handlePackageServiceChange}
                  />
                </Form.Item>
              )}

              <Form.Item
                name={ownerField}
                label={isPackageMode ? 'Package' : 'Service'}
                {...standardFormItemProps}
                rules={[
                  {
                    required: true,
                    message: `${isPackageMode ? 'Package' : 'Service'} is required`,
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={inputStyle}
                  options={ownerOptions}
                  placeholder={
                    isPackageMode ? 'Select package after selecting service' : 'Select service'
                  }
                  onChange={handleOwnerChange}
                  disabled={isPackageMode && !selectedServiceId}
                />
              </Form.Item>

              {requiresParameters &&
                selectedParameters.map(parameter => (
                  <Form.Item
                    key={parameter}
                    name={parameter}
                    label={parameterLabels[parameter] || parameter}
                    {...standardFormItemProps}
                    colon={false}
                    rules={[
                      {
                        required: true,
                        message: `${parameterLabels[parameter] || parameter} is required`,
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={inputStyle}
                      options={(countryOptionsByParameter as any)[parameter] || []}
                      placeholder={`Select one or more ${parameterLabels[parameter] || parameter}`}
                    />
                  </Form.Item>
                ))}
            </Accordian>

            <Accordian title="Heading">
              <Form.Item
                name="headingTitle"
                label="Heading Title"
                {...standardFormItemProps}
                extra="Page heading on the frontend. Leave blank to use the service name."
              >
                <Input placeholder="e.g. OCI Card Application" style={inputStyle} maxLength={255} />
              </Form.Item>

              <Form.Item
                name="headingDescription"
                label="Social Proof Text"
                {...standardFormItemProps}
                extra="Text shown next to the avatar icons below the heading (e.g. 46,500+ Travel Documents processed by Documitra)"
              >
                <Input.TextArea
                  placeholder="e.g. 46,500+ Travel Documents processed by Documitra"
                  style={inputStyle}
                  rows={2}
                  maxLength={500}
                />
              </Form.Item>
            </Accordian>

            <Accordian title="Steps">
              <Form.Item
                name="stepsHeading"
                label="Title"
                {...standardFormItemProps}
                extra="Heading shown above the steps section on the frontend (e.g. Application Steps - When You Choose Us)"
              >
                <Input
                  placeholder="e.g. Application Steps - When You Choose Us"
                  style={inputStyle}
                  maxLength={255}
                />
              </Form.Item>

              <Form.Item
                name="stepsSubtitle"
                label="Description"
                {...standardFormItemProps}
                extra="Subtitle shown below the steps heading on the frontend"
              >
                <Input.TextArea
                  placeholder="e.g. Follow the process in a clear sequence."
                  style={inputStyle}
                  rows={2}
                  maxLength={500}
                />
              </Form.Item>

              <Form.List name="steps">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        key={field.key}
                        label={`Step ${index + 1}`}
                        {...wideFormItemProps}
                        required
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'description']}
                            rules={[{ required: true, message: 'Step description required' }]}
                            style={{ marginBottom: 0, width: '100%', padding: 0, border: 'none' }}
                          >
                            <Input.TextArea
                              rows={2}
                              placeholder="Step description"
                              style={textAreaStyle}
                            />
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button danger onClick={() => remove(field.name)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </Form.Item>
                    ))}
                    <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                      <Button
                        type="dashed"
                        onClick={() => add({ sequence: fields.length + 1, description: '' })}
                        style={{ color: '#637f92', fontWeight: 600 }}
                      >
                        + Add Step
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Accordian>

            <Accordian title="About / Guide Content">
              <AboutMainSectionFields />
            </Accordian>

            <Accordian title="Content Blocks">
              <ContentBlocksSectionFields />
            </Accordian>

            <Accordian title="Before You Apply – Key Requirements">
              <KeyRulesSectionFields />
            </Accordian>

            <Accordian title="Benefits of the Service (Optional)">
              <BenefitsSectionFields />
            </Accordian>

            <Accordian title="Service Fees (Optional)">
              <FeesSectionFields />
            </Accordian>

            <Accordian title="Related Services">
              <RelatedServicesSectionFields />
            </Accordian>

            {mode === 'serviceNoParams' && (
              <Accordian title="Popular Services">
                <PopularServicesSectionFields />
              </Accordian>
            )}

            <Accordian title="FAQs">
              <Form.List name="faqs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        key={field.key}
                        label={`FAQ ${index + 1}`}
                        {...wideFormItemProps}
                        required
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            width: '100%',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'question']}
                              rules={[{ required: true, message: 'FAQ title required' }]}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Input placeholder="FAQ Question" />
                            </Form.Item>
                            {fields.length > 1 && (
                              <Button danger onClick={() => remove(field.name)}>
                                Remove
                              </Button>
                            )}
                          </div>
                          <Form.Item
                            {...field}
                            name={[field.name, 'answer']}
                            rules={[{ required: true, message: 'FAQ answer required' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              rows={3}
                              placeholder="FAQ Answer"
                              style={textAreaStyle}
                            />
                          </Form.Item>
                        </div>
                      </Form.Item>
                    ))}
                    <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({ sequence: fields.length + 1, question: '', answer: '' })
                        }
                        style={{ color: '#637f92', fontWeight: 600 }}
                      >
                        + Add FAQ
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Accordian>

            <Accordian title="Timeline">
              <TimelineSectionFields />
            </Accordian>

            {mode === 'serviceNoParams' && (
              <Accordian title="Related Articles">
                <RelatedArticlesSectionFields />
              </Accordian>
            )}
          </Form>
        </div>
      </div>

      <div className="btn-container">
        <div className="publish-btn-container">
          <div className="publish-btn">
            <InvertFillButton
              variant="blue"
              size="md"
              fontWeight="semibold"
              disabled={submitting}
              onClick={() => form.submit()}
            >
              Create
            </InvertFillButton>
          </div>
          <InvertFillButton
            variant="white"
            size="md"
            fontWeight="semibold"
            onClick={() => closeCreateForm()}
          >
            Cancel
          </InvertFillButton>
        </div>
      </div>
    </div>
  );

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName={headerName}
      updateFormVisibility={updateFormVisibility}
      onTabClick={setActiveKey}
      secondTabTitle={`Add ${addName}`}
      showFormComponent={showFormComponent}
      formComponent={formComponent}
      content={contentTabs}
    >
      <div className="abstractLanding">
        <DefaultTableHeader
          headerName={headerName}
          totalCount={totalCount}
          countText="records found"
          setSearchQuery={setSearchQuery}
          showFilter={true}
          filterQuery={setFilterQuery}
          onAdd={openCreateForm}
          addName={addName}
          entityName={endpoint}
          prefetchedFilterOptions={prefetchedFilterOptions}
        />
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={tableRecords}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
        <ConfirmationModal
          open={deleteModalOpen}
          message="Are you sure you want to delete?"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          onClose={handleCancelDelete}
        />
      </div>
    </AbstractTabs>
  );
};

export default ContentManagementPage;
